import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SITE_URL,
  SITE_NAME,
  SITE_LOGO_URL,
  getRouteKey,
  resolvePageSeo,
} from '@/lib/seo';

function setMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setCanonical(url: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

function removeCanonical() {
  const el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (el) el.remove();
}

function setRobots(content: string | null) {
  let el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (content === null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'robots');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export default function SeoManager() {
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const routeKey = getRouteKey(location.pathname);
    const canonical = `${SITE_URL}${location.pathname}`;

    let title: string;
    let description: string;
    let keywords: string;

    if (!routeKey) {
      title = t('meta.homeTitle');
      description = t('meta.homeDesc');
      keywords =
        'AllChinaBuy spreadsheet, AllChinaBuy spreadsheet 2026, AllChinaBuy spreadsheet list, AllChinaBuy buying guide, AllChinaBuy seller spreadsheet';
    } else {
      const pageSeo = resolvePageSeo(routeKey);
      if (pageSeo) {
        title = pageSeo.title;
        description = pageSeo.description;
        keywords = pageSeo.keywords;
      } else {
        title = '404 Not Found | AllChinaBuy Spreadsheet Hub';
        description = 'The page you are looking for does not exist.';
        keywords = 'AllChinaBuy spreadsheet';
      }
    }

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywords);

    if (resolvePageSeo(routeKey) || !routeKey) {
      setCanonical(canonical);
      setRobots(null);
    } else {
      removeCanonical();
      setRobots('noindex, nofollow');
    }

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:image', SITE_LOGO_URL);
    setMeta('property', 'og:site_name', SITE_NAME);

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', SITE_LOGO_URL);
  }, [location.pathname, t]);

  return null;
}